

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Container, Typography, IconButton, Stack } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import { API_URL } from "../config";

const TheoryQuestionsPage = () => {
  const navigate = useNavigate();
  const { theoryYearId } = useParams();

  const [yearData, setYearData] = useState({});
  const [showAnswers, setShowAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tokenString = localStorage.getItem("authToken");
        if (!tokenString) throw new Error("No token");

        const token = JSON.parse(tokenString);

        // Fetch full year data with numbers + questions
        const res = await fetch(
          `${API_URL}/theory-numbers/${theoryYearId}/numbers`,
          {
            headers: { Authorization: `Bearer ${token.access}` },
          }
        );

        if (!res.ok) throw new Error("Failed to load data");

        const data = await res.json();
        setYearData(data.theory_year || {}); // Set year with embedded numbers/questions
      } catch (err) {
        console.error(err);
        setError("Failed to load questions. Check your connection.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [theoryYearId]);

  const toggleAnswer = (index) => {
    setShowAnswers((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // ── Render latex ───────────────────────────────────────────────────────────
  const renderMath = (text) => {
    if (!text) return null;

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      // $$...$$ (block)
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \[...\] (block)
      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \(...\) (inline)
      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return <InlineMath key={index} math={part.slice(2, -2)} />;
      }

      // $...$ (inline)
      if (part.startsWith("$") && part.endsWith("$")) {
        return <InlineMath key={index} math={part.slice(1, -1)} />;
      }

      // Normal text
      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8f7f4",
        fontFamily: "'Sora',sans-serif",
        color: "#1a1a2e",
        backgroundImage:
          "linear-gradient(rgba(99,60,180,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(99,60,180,0.035) 1px,transparent 1px)",
        backgroundSize: "44px 44px",
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          position: "relative",
          bgcolor: "#fff",
          borderBottom: "1px solid rgba(26,26,46,0.07)",
          px: { xs: 2.5, md: 4 },
          pt: "3px",
          pb: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background:
              "linear-gradient(90deg,#633cb4,#a855f7,#e879f9,#633cb4)",
            backgroundSize: "300% 100%",
            animation: "stripe 4s linear infinite",
            "@keyframes stripe": { to: { backgroundPosition: "-300% 0" } },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2.75 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              flexShrink: 0,
              border: "1.5px solid rgba(99,60,180,0.2)",
              bgcolor: "rgba(99,60,180,0.05)",
              color: "#633cb4",
              "&:hover": { bgcolor: "rgba(99,60,180,0.12)" },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontSize: "1.35rem",
                fontWeight: 700,
                color: "#1a1a2e",
                letterSpacing: "-0.02em",
                mb: 0.25,
              }}
            >
              Theory Questions
            </Typography>
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.67rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(26,26,46,0.38)",
              }}
            >
              All questions for this year
            </Typography>
          </Box>

          {/* Year badge */}
          <Box
            sx={{
              background:
                "linear-gradient(135deg,rgba(99,60,180,0.08),rgba(168,85,247,0.06))",
              border: "1px solid rgba(99,60,180,0.15)",
              borderRadius: "10px",
              px: 1.75,
              py: 1,
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "1.1rem",
                fontWeight: 700,
                color: "#633cb4",
                display: "block",
              }}
            >
              {yearData.year ?? "—"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.62rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(99,60,180,0.5)",
              }}
            >
              Year
            </Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        {/* Loading */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 14,
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "2.5px solid rgba(99,60,180,0.12)",
                borderTopColor: "#a855f7",
                animation: "spin 0.9s linear infinite",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
              }}
            />
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.68rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "rgba(99,60,180,0.4)",
              }}
            >
              Loading questions
            </Typography>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: "12px",
              bgcolor: "rgba(239,68,68,0.06)",
              border: "1px solid rgba(239,68,68,0.2)",
              color: "#dc2626",
              fontSize: "0.88rem",
            }}
          >
            {error}
          </Box>
        )}

        {/* Empty state */}
        {!loading && !error && yearData.theory_numbers?.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
              gap: 1.25,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "3rem", mb: 0.5 }}>📭</Typography>
            <Typography
              sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#1a1a2e" }}
            >
              Coming Soon
            </Typography>
            <Typography
              sx={{ fontSize: "0.82rem", color: "rgba(26,26,46,0.4)" }}
            >
              No questions available yet for this year
            </Typography>
          </Box>
        )}

        {/* Questions list */}
        {!loading && !error && yearData.theory_numbers?.length > 0 && (
          <>
            {/* Section label */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  bgcolor: "#633cb4",
                }}
              />
              <Typography
                sx={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.67rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "#633cb4",
                  fontWeight: 500,
                }}
              >
                {yearData.theory_numbers.length} Question
                {yearData.theory_numbers.length !== 1 ? "s" : ""}
              </Typography>
            </Box>

            <Stack spacing={1.5}>
              {yearData.theory_numbers.map((num) => (
                <Box key={num.id}>
                  {num.questions?.length === 0
                    ? null
                    : num.questions.map((q) => (
                        <Box
                          key={q.id}
                          sx={{
                            bgcolor: "#fff",
                            border: "1.5px solid rgba(26,26,46,0.09)",
                            borderRadius: "16px",
                            overflow: "hidden",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            transition: "box-shadow 0.2s",
                            "&:hover": {
                              boxShadow: "0 6px 20px rgba(99,60,180,0.09)",
                            },
                          }}
                        >
                          {/* Accordion summary row */}
                          <Box
                            onClick={() => toggleAnswer(q.id)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              px: 2.5,
                              py: 2,
                              cursor: "pointer",
                              borderBottom: showAnswers[q.id]
                                ? "1px solid rgba(26,26,46,0.07)"
                                : "none",
                              transition: "background 0.2s",
                              "&:hover": { bgcolor: "rgba(99,60,180,0.02)" },
                            }}
                          >
                            {/* Q number badge */}
                            <Box
                              sx={{
                                flexShrink: 0,
                                bgcolor: "rgba(99,60,180,0.07)",
                                border: "1px solid rgba(99,60,180,0.15)",
                                borderRadius: "8px",
                                px: 1.25,
                                py: 0.5,
                                fontFamily: "'JetBrains Mono',monospace",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                color: "#633cb4",
                              }}
                            >
                              Q{num.number}
                            </Box>

                            {/* Question preview */}
                            <Typography
                              sx={{
                                flex: 1,
                                fontSize: "0.88rem",
                                fontWeight: 600,
                                color: "#1a1a2e",
                                lineHeight: 1.5,
                                display: "-webkit-box",
                                WebkitLineClamp: showAnswers[q.id]
                                  ? "unset"
                                  : 2,
                                WebkitBoxOrient: "vertical",
                                overflow: showAnswers[q.id]
                                  ? "visible"
                                  : "hidden",
                              }}
                            >
                              {renderMath(q.question)}
                            </Typography>

                            {/* Chevron */}
                            <Box
                              sx={{
                                flexShrink: 0,
                                width: 28,
                                height: 28,
                                borderRadius: "8px",
                                border: "1px solid rgba(26,26,46,0.1)",
                                bgcolor: "rgba(26,26,46,0.03)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                color: "rgba(26,26,46,0.4)",
                                transition: "transform 0.25s, background 0.2s",
                                transform: showAnswers[q.id]
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                              }}
                            >
                              ▼
                            </Box>
                          </Box>

                          {/* Expanded body */}
                          {showAnswers[q.id] && (
                            <Box sx={{ px: 2.5, pb: 2.5, pt: 2 }}>
                              {/* Question image */}
                              {q.quesImg && (
                                <Box
                                  sx={{
                                    mb: 2.5,
                                    borderRadius: "10px",
                                    overflow: "hidden",
                                    border: "1px solid rgba(26,26,46,0.08)",
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={q.quesImg}
                                    alt="Question"
                                    sx={{ width: "100%", display: "block" }}
                                  />
                                </Box>
                              )}

                              {/* Full question text (if truncated above) */}
                              <Typography
                                sx={{
                                  fontSize: "0.88rem",
                                  lineHeight: 1.7,
                                  color: "rgba(26,26,46,0.75)",
                                  mb: 2,
                                }}
                              >
                                {renderMath(q.question)}
                              </Typography>

                              {/* Divider */}
                              <Box
                                sx={{
                                  height: "1px",
                                  bgcolor: "rgba(26,26,46,0.07)",
                                  mb: 2,
                                }}
                              />

                              {/* Answer section label */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: "50%",
                                    bgcolor: "#16a34a",
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontFamily: "'JetBrains Mono',monospace",
                                    fontSize: "0.63rem",
                                    letterSpacing: "0.14em",
                                    textTransform: "uppercase",
                                    color: "#16a34a",
                                    fontWeight: 600,
                                  }}
                                >
                                  Answer
                                </Typography>
                              </Box>

                              {/* Answer box */}
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: "12px",
                                  bgcolor: "rgba(34,197,94,0.05)",
                                  border: "1px solid rgba(34,197,94,0.18)",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.88rem",
                                    lineHeight: 1.75,
                                    color: "rgba(26,46,26,0.8)",
                                  }}
                                >
                                  {renderMath(q.answer)}
                                </Typography>
                                {q.ansImg && (
                                  <Box
                                    sx={{
                                      mt: 2,
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      border: "1px solid rgba(34,197,94,0.15)",
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={q.ansImg}
                                      alt="Answer"
                                      sx={{ width: "100%", display: "block" }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Container>

      {/* Footer */}
      <Box
        sx={{
          textAlign: "center",
          py: 4,
          mt: 2,
          borderTop: "1px solid rgba(26,26,46,0.06)",
        }}
      >
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.65rem",
            letterSpacing: "0.12em",
            color: "rgba(26,26,46,0.3)",
          }}
        >
          © {new Date().getFullYear()} LiteUpper • Made with passion in Nigeria
        </Typography>
      </Box>
    </Box>
  );
};

export default TheoryQuestionsPage;
